/** A session-only account cache. Invalidations also revoke outstanding responses. */
export class CatalogueCache<T> {
  private owner: string | null = null;
  private revision = 0;
  private entry: { value: T; expiresAt: number } | null = null;
  private pending: { revision: number; promise: Promise<T | null> } | null = null;

  constructor(
    private readonly fetchValue: () => Promise<T>,
    private readonly expiresAt: (value: T, now: number) => number,
    private readonly now: () => number = Date.now,
  ) {}

  reset(): void {
    this.owner = null;
    this.invalidate();
  }

  invalidate(): void {
    this.revision++;
    this.entry = null;
    this.pending = null;
  }

  peek(accountId: string): T | null {
    return this.owner === accountId && this.entry && this.entry.expiresAt > this.now()
      ? this.entry.value
      : null;
  }

  load(accountId: string): Promise<T | null> {
    if (accountId !== this.owner) {
      this.invalidate();
      this.owner = accountId;
    }
    const hit = this.peek(accountId);
    if (hit !== null) return Promise.resolve(hit);
    if (this.pending) return this.pending.promise;
    const revision = this.revision;
    const promise = this.fetchValue()
      .then((value) => {
        if (this.owner !== accountId || this.revision !== revision) return null;
        this.entry = { value, expiresAt: this.expiresAt(value, this.now()) };
        return value;
      })
      .finally(() => {
        if (this.pending?.revision === revision) this.pending = null;
      });
    this.pending = { revision, promise };
    return promise;
  }
}
